/**
 * MatterportAttachPanel — landlord UI to attach a Matterport 3D model to
 * one of their published listings, and to see monthly usage vs quota.
 * Renders inline on the Smart Tours page.
 */
import { useEffect, useState, useCallback } from "react";
import { Card, Button, Pill, Input, Modal } from "@/components/ui/Primitives";
import { Box, Plus, Trash2, ExternalLink, TrendingUp, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("crc_session_token") || ""}`,
});

export default function MatterportAttachPanel({ listings, uid, onRefresh }) {
  const [usage, setUsage] = useState(null);
  const [open, setOpen] = useState(null); // listing
  const [form, setForm] = useState({ tour_ref: "", title: "" });
  const [busy, setBusy] = useState(false);

  const loadUsage = useCallback(async () => {
    if (!uid) return;
    try {
      const r = await fetch(`${API}/api/matterport/usage`, { headers: authHeaders() });
      if (r.ok) setUsage(await r.json());
    } catch { /* silent */ }
  }, [uid]);

  useEffect(() => { loadUsage(); }, [loadUsage]);

  async function saveAttach(e) {
    e.preventDefault();
    if (!open) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/matterport/listing/${open.id}/attach`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          tour_ref: form.tour_ref.trim(),
          title: form.title.trim() || null,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not attach 3D tour");
      toast.success("3D tour attached");
      setOpen(null);
      setForm({ tour_ref: "", title: "" });
      onRefresh && onRefresh();
      loadUsage();
    } catch (err) { toast.error(String(err.message)); }
    finally { setBusy(false); }
  }

  async function detach(listing) {
    if (!window.confirm("Remove the 3D tour from this listing?")) return;
    try {
      const r = await fetch(`${API}/api/matterport/listing/${listing.id}/attach`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (!r.ok) throw new Error("Could not remove");
      toast.success("3D tour removed");
      onRefresh && onRefresh();
    } catch (err) { toast.error(String(err.message)); }
  }

  const quota = usage?.quota ?? 0;
  const used = usage?.used ?? 0;
  const pct = quota > 0 ? Math.min(100, Math.round((used / quota) * 100)) : 0;
  const noPlan = quota === 0;

  return (
    <div className="mb-8" data-testid="matterport-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2">
          <Box className="w-4 h-4 text-emerald-700" /> 3D walkthroughs (Matterport)
        </h3>
        {usage && (
          <div className="flex items-center gap-2 text-xs text-slate-600" data-testid="matterport-usage-summary">
            <TrendingUp className="w-3.5 h-3.5" />
            <span>{used}/{quota === 999 ? "∞" : quota} views this month</span>
          </div>
        )}
      </div>

      {noPlan ? (
        <Card className="p-5 border-amber-200 bg-amber-50 mb-3" data-testid="matterport-upgrade-cta">
          <div className="flex flex-col sm:flex-row items-start gap-4">
            <div className="rounded-xl bg-amber-100 p-3 shrink-0">
              <Sparkles className="w-5 h-5 text-amber-700" />
            </div>
            <div className="flex-1 text-sm">
              <div className="font-display text-base font-bold text-amber-900">Unlock 3D property tours</div>
              <p className="mt-1 text-amber-800">Attach immersive Matterport walkthroughs to your listings so prospective tenants can self-tour 24/7. Requires an active plan.</p>
              <Link to="/pricing"><Button className="mt-3 !py-1.5 !px-3 text-xs">See plans</Button></Link>
            </div>
          </div>
        </Card>
      ) : (
        <Card className="p-4 mb-3">
          <div className="flex items-center justify-between text-xs text-slate-600">
            <span className="font-semibold">Monthly usage</span>
            <span data-testid="matterport-usage-count">{used} / {quota === 999 ? "unlimited" : quota}</span>
          </div>
          <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
            <div
              className={`h-full ${used >= quota ? "bg-rose-500" : used / quota > 0.75 ? "bg-amber-500" : "bg-emerald-500"}`}
              style={{ width: `${quota === 999 ? Math.min(100, used) : pct}%` }}
            />
          </div>
          {used >= quota && quota !== 999 && (
            <div className="mt-2 text-xs text-rose-600">
              Quota reached — public 3D tours are paused until next billing cycle or an upgrade.{" "}
              <Link to="/pricing" className="font-bold underline">Upgrade</Link>
            </div>
          )}
        </Card>
      )}

      {listings.length === 0 ? null : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {listings.map((l) => (
            <Card key={l.id} className="p-4" data-testid={`matterport-listing-${l.id}`}>
              <div className="font-bold text-slate-900 truncate">{l.title}</div>
              <div className="text-xs text-slate-500 mt-0.5 mb-3">{l.city}, {l.province}</div>
              {l.matterport_model_id ? (
                <div className="space-y-2">
                  <Pill tone="green"><Box className="w-3 h-3 inline mr-1" /> 3D tour attached</Pill>
                  <div className="text-[11px] text-slate-500 font-mono truncate">{l.matterport_model_id}</div>
                  <div className="flex items-center gap-3 text-xs">
                    <a
                      href={`https://my.matterport.com/show/?m=${encodeURIComponent(l.matterport_model_id)}`}
                      target="_blank" rel="noreferrer"
                      className="font-bold text-emerald-700 inline-flex items-center gap-1 hover:underline"
                      data-testid={`matterport-preview-${l.id}`}
                    >
                      <ExternalLink className="w-3 h-3" /> Preview
                    </a>
                    <button
                      onClick={() => { setForm({ tour_ref: l.matterport_model_id, title: l.matterport_title || "" }); setOpen(l); }}
                      className="font-bold text-slate-600 hover:text-slate-900"
                      data-testid={`matterport-edit-${l.id}`}
                    >Replace</button>
                    <button
                      onClick={() => detach(l)}
                      className="font-bold text-rose-600 hover:text-rose-700 inline-flex items-center gap-1"
                      data-testid={`matterport-remove-${l.id}`}
                    ><Trash2 className="w-3 h-3" /> Remove</button>
                  </div>
                </div>
              ) : (
                <Button
                  onClick={() => { setForm({ tour_ref: "", title: "" }); setOpen(l); }}
                  disabled={noPlan}
                  className="!py-1.5 !px-3 text-xs"
                  data-testid={`matterport-add-${l.id}`}
                >
                  <Plus className="w-3 h-3 inline mr-1" /> Attach 3D tour
                </Button>
              )}
            </Card>
          ))}
        </div>
      )}

      <Modal
        open={!!open}
        onClose={() => setOpen(null)}
        title={`Attach 3D tour — ${open?.title || ""}`}
        testid="matterport-modal"
      >
        <form onSubmit={saveAttach} className="space-y-4">
          <Input
            label="Matterport share URL or model ID"
            required
            placeholder="https://my.matterport.com/show/?m=SxQL3iGyoDo"
            value={form.tour_ref}
            onChange={(e) => setForm({ ...form, tour_ref: e.target.value })}
            testid="matterport-ref-input"
          />
          <p className="text-xs text-slate-500 -mt-2">
            Paste either the full share URL or the 11-character model ID from your Matterport dashboard.
          </p>
          <Input
            label="Tour title (optional)"
            placeholder="Full walkthrough of the 2-bedroom"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            testid="matterport-title-input"
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setOpen(null)}>Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="matterport-save">
              {busy ? "Saving…" : "Attach tour"}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
